import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
  Comment,
  Delete as DeleteIcon,
  Edit,
  PlaylistAdd,
} from "@mui/icons-material";
import { useState } from "react";
import type { MenuOptionType } from "@/controls/contextMenu/models/MenuOptionType";
import type { StrandLossByShift } from "../models/StrandLossByShift";

export const STRAND_LOSS_MODAL = {
  NEW: "new",
  EDIT: "edit",
  DELETE: "delete",
  JUSTIFY: "justify",
  CLOSED: "",
} as const;

export type StrandLossModal =
  (typeof STRAND_LOSS_MODAL)[keyof typeof STRAND_LOSS_MODAL];

interface UseStrandLossesOptionFunctionsProps {
  setSelectedId: (id: number | null) => void;
  setShowModal: (show: StrandLossModal) => void;
}

export const useStrandLossesOptionFunctions = ({
  setSelectedId,
  setShowModal,
}: UseStrandLossesOptionFunctionsProps) => {
  const [messageDeleteModal, setMessageDeleteModal] = useState("");

  const createData = () => setShowModal(STRAND_LOSS_MODAL.NEW);

  const editData = (data: StrandLossByShift) => {
    setSelectedId(data.StrandLossId);
    if (data.IsManual) setShowModal(STRAND_LOSS_MODAL.EDIT);
  };

  const justifyData = (data: StrandLossByShift) => {
    setSelectedId(data.StrandLossId);
    setShowModal(STRAND_LOSS_MODAL.JUSTIFY);
  };

  const deleteData = (data: StrandLossByShift) => {
    setSelectedId(data.StrandLossId);
    const dateOpen = dxtToLocalServerTime(data.Start, "HH:mm:ss");
    setMessageDeleteModal(
      `The Strand Loss at ${dateOpen} will be deleted. Do you want to continue?`,
    );
    setShowModal(STRAND_LOSS_MODAL.DELETE);
  };

  const getMenuOptions = (data: StrandLossByShift) => {
    const options: MenuOptionType[] = [];
    if (data) {
      options.push(
        {
          name: "Justify Strand Loss",
          key: "justify_strand_loss",
          onClick: () => justifyData(data),
          icon: <Comment />,
        },
        {
          name: "Edit Strand Loss",
          key: "edit_strand_loss",
          onClick: () => editData(data),
          icon: <Edit />,
          disabled: !data.IsManual,
        },
        {
          name: "Delete Strand Loss",
          key: "delete_strand_loss",
          onClick: () => deleteData(data),
          icon: <DeleteIcon />,
          disabled: !data.IsManual,
        },
      );
    }
    options.push({
      name: "New Strand Loss",
      key: "create_new_strand_loss",
      onClick: createData,
      icon: <PlaylistAdd />,
    });
    return options;
  };

  return {
    createData,
    justifyData,
    getMenuOptions,
    messageDeleteModal,
    setMessageDeleteModal,
  };
};
